'use client';

import * as flags from 'country-flag-icons/react/3x2';

import { useRef, useState, type JSX, type ChangeEvent } from 'react';

import { Text } from '~/components/text';
import { Input } from '~/components/input';
import { Separator } from '~/components/separator';
import { CaretDownIcon } from '~/components/icons';
import { RadioGroup, RadioGroupItem } from '~/components/radio-group';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/popover';
import {
  cn,
  combine,
  getFullCountries,
  formatPhoneNumber,
  correctPhoneNumber,
  sanitizeDefaultPhoneNumber,
  type TCode,
  type ICountryProps,
} from '~/utils';
import {
  Command,
  CommandList,
  CommandItem,
  CommandGroup,
  CommandInput,
} from '~/components/command';

interface IOnCodeChangeParams {
  code: TCode;
  country: string;
}

interface IInputPhoneProps {
  disabled?: boolean;
  onChange?: (value: string) => void;
  onCodeChange?: (value: IOnCodeChangeParams) => void;
  defaultValue?: {
    code?: TCode;
    number?: string;
    country?: string;
  };
}

export function InputPhone({
  disabled = false,
  onChange,
  onCodeChange,
  defaultValue: defaultValueProp,
  ...rest
}: IInputPhoneProps): JSX.Element {
  const defaultValue = sanitizeDefaultPhoneNumber(defaultValueProp);
  const countriesRef = useRef(getFullCountries());
  const groupedCountriesRef = useRef(
    countriesRef.current.reduce<Record<string, ICountryProps>>(
      (result, country) => {
        result[country.code] = country;

        return result;
      },
      {},
    ),
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const [displayedNumber, setDisplayedNumber] = useState(
    formatPhoneNumber(defaultValue.number),
  );
  const [country, setCountry] = useState<ICountryProps>(
    groupedCountriesRef.current[defaultValue.country] ||
      countriesRef.current[0],
  );

  const SelectedFlag = flags[country.code as keyof typeof flags];
  const selectedPhoneCode = country.phoneCode;

  const handleChangePhoneNumber = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(correctPhoneNumber(event.target.value).replace(/ /g, ''));
    setDisplayedNumber(formatPhoneNumber(event.target.value));
  };

  const handleSelectCountry: ICountryItemProps['onSelect'] = (country) => {
    setCountry(country);
    onCodeChange?.({
      country: country.code,
      code: country.phoneCode,
    });
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  return (
    <div
      {...rest}
      ref={containerRef}
      className={cn(
        'flex border border-[#ececec] rounded-md items-center gap-3 py-[12.5px] h-[46px]',
        'hover:border-[#cdcdcd] aria-invalid:border-desire-500',
        (open || focused) &&
          'border-blueBonnet-500 hover:border-blueBonnet-500',
      )}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          disabled={disabled}
          tabIndex={-1}
          className='flex gap-2 items-center min-w-[116px] pl-4 cursor-pointer'
        >
          <div className='flex gap-2 items-center flex-1'>
            <SelectedFlag className='!w-[40px] !h-[20px]' />
            <Text className='text-[16px] leading-[130%]'>
              {selectedPhoneCode}
            </Text>
          </div>
          <CaretDownIcon className='size-4' />
        </PopoverTrigger>
        <PopoverContent
          align='start'
          side='bottom'
          sideOffset={19.5}
          className='p-0 w-full'
          style={{
            width: containerRef.current
              ? `${containerRef.current.offsetWidth}px`
              : '100%',
          }}
        >
          <Command className='w-full'>
            <CommandInput
              placeholder='Search country code...'
              wrapperClassName='py-3 px-4 h-[unset]'
              className='p-0 h-[unset] text-(length:--text-md) leading-[130%]'
            />
            <CommandList>
              <CommandGroup className='p-0'>
                <RadioGroup className='gap-0' value={country.code}>
                  {countriesRef.current.map((c) => {
                    return (
                      <CountryItem
                        country={c}
                        key={c.code}
                        onSelect={handleSelectCountry}
                        selected={c.code === country.code}
                      />
                    );
                  })}
                </RadioGroup>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Separator orientation='vertical' className='min-h-[22px]' />
      <Input
        type='tel'
        disabled={disabled}
        maxLength={14}
        onBlur={handleBlur}
        onFocus={handleFocus}
        value={displayedNumber}
        placeholder='000 000 000'
        onChange={handleChangePhoneNumber}
        className={cn(
          'border-0 p-0 pr-4',
          'placeholder:text-[#bfbfbf] shadow-none h-[unset]',
        )}
      />
    </div>
  );
}

interface ICountryItemProps {
  selected?: boolean;
  country: ICountryProps;
  onSelect?: (country: ICountryProps) => void;
}

function CountryItem({
  country,
  selected,
  onSelect,
}: ICountryItemProps): JSX.Element {
  const { code, name, phoneCode } = country;
  const Flag = flags[country.code as keyof typeof flags];

  const handleSelectCountry = () => {
    onSelect?.(country);
  };

  return (
    <CommandItem
      key={phoneCode}
      onSelect={handleSelectCountry}
      value={combine(
        {
          joinWith: ' ',
        },
        name,
        phoneCode,
      )}
      className={cn(
        'items-center gap-2 px-4 py-[12.5px] data-[selected=true]:bg-[#f9f9f9]',
        selected && 'bg-[#f9f9f9]',
      )}
    >
      <div className='flex flex-1 gap-2 items-center w-full'>
        <Flag className='!w-[40px] !h-[20px]' />
        <Text className='text-[16px] leading-[130%]'>{phoneCode}</Text>
        <Text className='text-[16px] leading-[130%]'>{name}</Text>
      </div>
      <RadioGroupItem
        id={code}
        value={code}
        indicatorIconClassName='size-3 fill-blueBonnet-500'
        className={cn(
          'h-5 w-5 border-blueBonnet-50 bg-blueBonnet-50',
          'data-[state=checked]:border-blueBonnet-500',
        )}
      />
    </CommandItem>
  );
}
